import React, {
  Fragment,
  useContext,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  Alert,
  ListVariant,
  LoginForm,
  LoginMainFooterBandItem,
  LoginPage,
  Spinner,
} from "@patternfly/react-core";

import { AuthContext } from "shared/context/AuthContext";


const MainLoginPage = (props) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [showHelperText, setShowHelperText] =
    useState(false);

  const [helperText, setHelperText] = useState(
    "Nieprawidłowe dane logowania."
  );

  const [isLoading, setIsLoading] =
    useState(false);

  const {
    setContext,
    loadSystemInfo,
  } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();

  const handleUsernameChange = (
    _event,
    value
  ) => {
    setUsername(value);
  };

  const handlePasswordChange = (
    _event,
    value
  ) => {
    setPassword(value);
  };

  const HelperTextBasic = (
    variant,
    message
  ) => (
    <Fragment>
      <Alert
        variant={variant}
        title={message}
        ouiaId={message}
      />
    </Fragment>
  );

  const getCSRFToken = () => {
    return document.cookie
      .split("; ")
      .find((row) =>
        row.startsWith("csrftoken=")
      )
      ?.split("=")[1];
  };

  const fetchCSRFToken = async () => {
    const response = await fetch(
      `${props.state.api_url}` +
        "/ti/api/get-csrf-token/",
      {
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(
        `CSRF request failed: ${response.status}`
      );
    }
  };

  const loginToIntegrations = async (
    currentPassword
  ) => {
    // login() w Django obraca token CSRF.
    await fetchCSRFToken();

    const csrfToken = getCSRFToken();

    if (!csrfToken) {
      throw new Error(
        "Brak aktualnego tokenu CSRF"
      );
    }

    const response = await fetch(
      `${props.state.api_url}` +
        "/ti/api/integrations/login/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          password: currentPassword,
        }),
        credentials: "include",
      }
    );

    if (!response.ok) {
      const responseText =
        await response.text();

      console.error(
        "Integrations login response:",
        response.status,
        responseText
      );

      throw new Error(
        `Integrations login failed: ` +
          `${response.status}`
      );
    }

    return response.json();
  };

  const saveUserData = (data) => {
    setContext({
      isAuthUser: true,
      isLoadedUserProfile: true,
      isOkdAvailable: null,
      isOvirtAvailable: null,
      areIntegrationsReady: false,
      userProfile: data.user,
    });
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!username || !password) {
      setShowHelperText(true);

      setHelperText(
        HelperTextBasic(
          "warning",
          "Uzupełnij nazwę użytkownika i hasło."
        )
      );

      return;
    }

    setIsLoading(true);

    try {
      await fetchCSRFToken();

      const csrfToken = getCSRFToken();

      if (!csrfToken) {
        throw new Error(
          "Brak tokenu CSRF"
        );
      }

      const resp = await fetch(
        `${props.state.api_url}` +
          "/ti/api/login/",
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            username,
            password,
          }),
          credentials: "include",
        }
      );

      if (!resp.ok) {
        let errorMsg;

        switch (resp.status) {
          case 400:
          case 401:
            errorMsg =
              "Nieprawidłowa nazwa użytkownika " +
              "lub hasło.";
            break;

          case 403:
            errorMsg =
              "Brak dostępu do serwera.";
            break;

          case 503:
            errorMsg =
              "Brak połączenia z LDAP.";
            break;

          case 500:
          default:
            errorMsg =
              "Błąd serwera, spróbuj " +
              "ponownie później.";
            break;
        }

        setShowHelperText(true);

        setHelperText(
          HelperTextBasic(
            "warning",
            errorMsg
          )
        );

        setIsLoading(false);
        return;
      }

      const data = await resp.json();

      saveUserData(data);

      setShowHelperText(false);
      setIsLoading(false);

      const redirectTo =
        location.state?.from || "/";

      // Rozpoczyna się przed odmontowaniem
      // komponentu LoginPage.
      const integrationsPromise =
        loginToIntegrations(password);

      navigate(redirectTo, {
        replace: true,
      });

      // Dane systemowe nie zależą od OKD/oVirt.
      void loadSystemInfo().catch(
        (error) => {
          console.error(
            "Nie udało się pobrać " +
              "danych systemowych:",
            error
          );
        }
      );

      // Logowanie do dodatkowych usług
      // nie blokuje interfejsu.
      void integrationsPromise
        .then((integrations) => {
          setContext({
            isOkdAvailable:
              integrations.okd_available,
            isOvirtAvailable:
              integrations.ovirt_available,
            areIntegrationsReady: true,
            ovirtSSOToken:
              integrations.ovirt_sso_token,
            ovirtWebadminUrl:
              integrations.ovirt_webadmin_url,
          });
        })
        .catch((error) => {
          console.error(
            "Nie udało się uruchomić " +
              "integracji:",
            error
          );
          setContext({
            isOkdAvailable: false,
            isOvirtAvailable: false,
            areIntegrationsReady: true,
          });
        });
    } catch (err) {
      console.error(
        "Login error:",
        err
      );

      setShowHelperText(true);

      setHelperText(
        HelperTextBasic(
          "error",
          "Nie można połączyć się z " +
            "serwerem, spróbuj ponownie."
        )
      );

      setIsLoading(false);
    }
  };

  const forgotCredentials = (
    <LoginMainFooterBandItem>
      <Link
        className="pf-v6-c-content--a"
        to="/password-reset"
      >
        Ustaw / Resetuj hasło użytkownika
      </Link>
    </LoginMainFooterBandItem>
  );

  const loginForm = (
    <LoginForm
      showHelperText={showHelperText}
      helperText={helperText}
      usernameLabel="Nazwa użytkownika"
      usernameValue={username}
      onChangeUsername={
        handleUsernameChange
      }
      passwordLabel="Hasło"
      passwordValue={password}
      onChangePassword={
        handlePasswordChange
      }
      onLoginButtonClick={handleLogin}
      loginButtonLabel={
        isLoading ? (
          <>
            <Spinner size="sm" />{" "}
            Logowanie ...
          </>
        ) : (
          "Zaloguj"
        )
      }
      isLoginButtonDisabled={isLoading}
    />
  );

  return (
    <div className="pf-login-wrapper">
      <LoginPage
        footerListVariants={
          ListVariant.inline
        }
        loginTitle="Zaloguj się na swoje konto"
        loginSubtitle="Wprowadź poświadczenia"
        forgotCredentials={forgotCredentials}
      >
        {loginForm}
      </LoginPage>
    </div>
  );
};

export default MainLoginPage;